import { ContentListShell } from "@/components/studio/content-list-shell";
import { LifecycleActionsMenu } from "@/components/studio/lifecycle-actions-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useContentLifecycle } from "@/hooks/use-content-lifecycle";
import { ContentAffirmationService } from "@/lib/api/affirmation.service";
import { categoryService } from "@/lib/api/categories.service";
import { coachService } from "@/lib/api/coachService";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { useAuth } from "@/lib/auth";
import type {
  Category,
  Coach,
  StudioContentStatus,
  TagGroup,
  TagOption,
} from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  formatDate,
  STATUS_CONFIG,
} from "@/lib/studio-constants";
import { useQuery } from "@tanstack/react-query";
import { Globe, Tags } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";

const affirmationConfig = CONTENT_TYPE_CONFIG.affirmation;
const PAGE_SIZE = 5;
const ELEVATED_ROLES = [
  "admin",
  "super_admin",
  "coach_manager",
  "content_manager",
];

interface AffirmationListItem {
  id: string;
  tagOptionIds: string[];
  status: StudioContentStatus;
  version: number;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  primaryLanguage: string;
  primaryQuote: string;
  languageCount: number;
  languageCodes: string[];
  tagCount: number;
  authorCoachId: string | null;
  authorCoachName: string | null;
  authorCoachAvatarUrl: string | null;
}

interface AffirmationListResponse {
  items: AffirmationListItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export default function AffirmationListPage() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const isCoachOnly =
    !!user?.roles?.includes("coach") &&
    !user.roles.some((r) => ELEVATED_ROLES.includes(r));
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, string>>({
    status: "all",
    category: "all",
    tag: "all",
    author: "all",
  });
  const [languageFilter, setLanguageFilter] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterValues, languageFilter]);

  const queryParams = useMemo(
    () => ({
      search: searchQuery || undefined,
      status:
        filterValues.status !== "all"
          ? (filterValues.status as StudioContentStatus)
          : undefined,
      categoryId:
        filterValues.category !== "all" ? filterValues.category : undefined,
      tagId: filterValues.tag !== "all" ? filterValues.tag : undefined,
      authorCoachId:
        filterValues.author !== "all" ? filterValues.author : undefined,
      language: languageFilter !== "all" ? languageFilter : undefined,
      page,
      limit: PAGE_SIZE,
    }),
    [searchQuery, filterValues, languageFilter, page],
  );

  const { data: response, isLoading } = useQuery<AffirmationListResponse>({
    queryKey: ["/content-affirmations", queryParams],
    queryFn: () =>
      ContentAffirmationService.findAll(
        queryParams,
      ) as unknown as Promise<AffirmationListResponse>,
  });

  const rawItems = (response?.items as any[]) ?? [];
  const totalItems = response?.pagination.total ?? 0;
  const totalPages = response?.pagination.totalPages ?? 1;

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const { data: coaches = [] } = useQuery<Coach[]>({
    queryKey: ["/api/coaches"],
    queryFn: () => coachService.getCoaches().then((res) => res.coaches),
    enabled: !isCoachOnly,
  });

  const { submitMutation, archiveMutation, withdrawMutation, restoreMutation } =
    useContentLifecycle({
      contentType: "affirmation",
      apiPath: "/content-affirmations",
      listRoute: "/studio/affirmations",
      editRoute: (id) => `/studio/affirmations/${id}/edit`,
    });

  const tagOptionsWithGroup = useMemo(
    () =>
      tagOptions.map((opt) => {
        const group = tagGroups.find((g) => g.id === opt.groupId);
        return { ...opt, groupName: group?.name ?? "" };
      }),
    [tagOptions, tagGroups],
  );

  const items: AffirmationListItem[] = useMemo(() => {
    return rawItems.map((item: any) => {
      const latestVersion = item.versions?.[0] || {};
      const languages = latestVersion.languages || [];
      const defaultLang =
        languages.find((l: any) => l.isDefault) || languages[0] || {};
      const authorCoachName =
        item.author?.name ||
        (item.authorCoachId
          ? (coaches.find((c) => c.id === item.authorCoachId)?.name ?? null)
          : null);
      const authorCoachAvatarUrl = item.author?.coachProfile?.avatarUrl ?? null;

      return {
        id: item.id,
        tagOptionIds: item.tagOptionIds || [],
        status: (latestVersion.status || item.status) as StudioContentStatus,
        version: latestVersion.version || item.version || 1,
        createdBy: item.createdBy || "",
        createdByName: item.createdByName || null,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        primaryLanguage: item.primaryLanguage || "en",
        primaryQuote: defaultLang.quoteText || "Untitled",
        languageCount: languages.length || 0,
        languageCodes: languages.map((l: any) => l.languageCode) || [],
        tagCount: item.tagOptionIds?.length || 0,
        authorCoachId: item.authorCoachId || null,
        authorCoachName,
        authorCoachAvatarUrl,
      };
    });
  }, [rawItems, coaches]);

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const customFilters = [
    {
      key: "category",
      label: "Categories",
      options: categories.map((cat) => ({ value: cat.id, label: cat.title })),
    },
    {
      key: "tag",
      label: "Tags",
      options: tagOptionsWithGroup.map((opt) => ({
        value: opt.id,
        label: opt.groupName ? `${opt.groupName}: ${opt.label}` : opt.label,
      })),
    },
    ...(isCoachOnly
      ? []
      : [
          {
            key: "author",
            label: "Authors",
            width: "160px",
            options: coaches.map((coach) => ({
              value: coach.id,
              label: coach.name,
            })),
          },
        ]),
  ];

  const truncateQuote = (text: string, maxLen = 80) => {
    if (!text || text.length <= maxLen) return text;
    return text.slice(0, maxLen) + "...";
  };

  const startIndex = totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + items.length, totalItems);

  return (
    <ContentListShell
      contentTypeConfig={affirmationConfig}
      isLoading={isLoading}
      itemCount={totalItems}
      searchQuery={searchInput}
      onSearchChange={setSearchInput}
      filters={customFilters}
      filterValues={filterValues}
      onFilterChange={handleFilterChange}
      showLanguageFilter={true}
      languageFilterValue={languageFilter}
      onLanguageFilterChange={setLanguageFilter}
      emptyStateMessage="No affirmations found"
      pagination={{
        currentPage: page,
        totalPages,
        totalItems,
        startIndex,
        endIndex,
        canGoNext: page < totalPages,
        canGoPrev: page > 1,
        onPageChange: setPage,
        onNext: () => setPage((p) => Math.min(p + 1, totalPages)),
        onPrev: () => setPage((p) => Math.max(p - 1, 1)),
      }}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40%]">Quote</TableHead>
            <TableHead>Author</TableHead>
            <TableHead>Tags</TableHead>
            <TableHead>Languages</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.draft;
            return (
              <TableRow
                key={item.id}
                className="cursor-pointer"
                onClick={() =>
                  setLocation(affirmationConfig.editRoute(item.id))
                }
                data-testid={`row-affirmation-${item.id}`}
              >
                <TableCell
                  className="font-medium"
                  data-testid={`text-quote-${item.id}`}
                >
                  {truncateQuote(item.primaryQuote)}
                  {item.version > 1 && (
                    <Badge
                      variant="outline"
                      className="ml-2 text-xs no-default-hover-elevate no-default-active-elevate"
                    >
                      v{item.version}
                    </Badge>
                  )}
                </TableCell>
                <TableCell data-testid={`text-author-${item.id}`}>
                  {item.authorCoachName ? (
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        {item.authorCoachAvatarUrl ? (
                          <AvatarImage
                            src={item.authorCoachAvatarUrl}
                            alt={item.authorCoachName}
                          />
                        ) : null}
                        <AvatarFallback className="text-[10px]">
                          {item.authorCoachName.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-muted-foreground">
                        {item.authorCoachName}
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">{"-"}</span>
                  )}
                </TableCell>
                <TableCell data-testid={`text-tags-${item.id}`}>
                  <div className="flex items-center gap-1">
                    <Tags className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {item.tagCount}
                    </span>
                  </div>
                </TableCell>
                <TableCell data-testid={`text-languages-${item.id}`}>
                  <div className="flex items-center gap-1">
                    <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {item.languageCount}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={statusCfg.variant}
                    className="no-default-hover-elevate no-default-active-elevate"
                    data-testid={`badge-status-${item.id}`}
                  >
                    {statusCfg.label}
                  </Badge>
                </TableCell>
                <TableCell
                  className="text-sm text-muted-foreground"
                  data-testid={`text-updated-${item.id}`}
                >
                  {formatDate(item.updatedAt)}
                </TableCell>
                <TableCell>
                  <LifecycleActionsMenu
                    itemId={item.id}
                    status={item.status}
                    createdBy={item.createdBy}
                    authorCoachId={item.authorCoachId}
                    onEdit={(id) =>
                      setLocation(affirmationConfig.editRoute(id))
                    }
                    onSubmit={(id) => submitMutation.mutate(id)}
                    onArchive={(id) => archiveMutation.mutate(id)}
                    onWithdraw={(id) => withdrawMutation.mutate(id)}
                    onRestore={(id) => restoreMutation.mutate(id)}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </ContentListShell>
  );
}
